"use client"

import { formatTimeAgo } from "@/lib/utils/format"
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ExternalLink, Heart, MessageCircle } from "lucide-react"
import { CommentResp } from "@types"
import Linkify from "react-linkify"
import { AuthorAvatar } from "@/app/components/Avatar"
import Image from "next/image"
import Farcaster from "public/publication/farcaster.png"
import DeleteCommentButton from "./DeleteCommentButton"
import EditCommentButton from "./EditCommentButton"
import EditComment from "./EditComment"
import AddComment from "./AddComment"
import { CommentProps } from "./types"
import { User } from "@/types/users"
import Link from "next/link"

/**
 * The maximum depth of comments to render.
 *
 * This is used to prevent too deeply nested comments from looking bad until we have a better UI to handle this.
 */
const MAX_DEPTH = 4

/**
 * A comment with its replies.
 *
 * It calls itself recursively to render all replies.
 */
export default function Comment(props: CommentProps) {
  const { comment, depth } = props

  // Don't render comments that are more than 5 levels deep, including the parent comment.
  if (depth > MAX_DEPTH) {
    return null
  }

  const partialUser: Partial<User> = {
    id: comment.userId,
    authorName: comment.authorName,
    avatar_url: comment.avatarUrl,
  }

  return (
    <Card className="mt-4 border-none shadow-none font-body">
      <CardHeader className="pb-2.5 pl-1 pt-0 pr-0">
        <div className="flex flex-row gap-2">
          <div className="relative">
            <AuthorAvatar
              user={partialUser}
              size={40}
              className="h-10 w-10 border border-white dark:border-black"
            />

            {comment.platform === "farcaster" && (
              <Image
                width={20}
                height={20}
                className="absolute -right-1 -bottom-1"
                src={Farcaster}
                alt="Farcaster"
              />
            )}
          </div>

          <CommentAuthorAndTime comment={comment} />
        </div>
      </CardHeader>
      <CardContent className="pb-0">
        <div className="ml-7 text-muted-foreground text-sm relative">
          <p className="prose prose-sm whitespace-pre-line dark:prose-invert">
            <Linkify
              componentDecorator={(decoratedHref, decoratedText, key) => (
                <a target="blank" href={decoratedHref} key={key}>
                  {decoratedText}
                </a>
              )}
            >
              {comment.text}
            </Linkify>
          </p>
        </div>
      </CardContent>

      <CommentFooter {...props} />

      <CommentThread {...props} />
    </Card>
  )
}

function CommentAuthorAndTime({ comment }: { comment: CommentResp }) {
  const timeAgo = formatTimeAgo(new Date(comment.createdAt))

  return (
    <div className="flex flex-col text-sm">
      <div className="font-semibold">{comment.authorName}</div>
      <div className="text-xs text-muted-foreground">Commented {timeAgo}</div>
    </div>
  )
}

function CommentFooter(props: CommentProps) {
  const {
    comment,
    depth,
    postId,
    refresh,
    isEditingCommentId,
    handleSetIsEditingCommentId,
    isEditInProgress,
    handleEditStart,
    handleEditEnd,
    isReplyingToCommentId,
    handleSetIsReplyingToCommentId,
    isReplyInProgress,
    handleReplyStart,
    handleReplyEnd,
  } = props

  return (
    <CardFooter className="ml-6 pb-0 flex flex-col items-start">
      <div className="flex flex-row">
        {/* Currently, only Farcaster supports likes. */}
        {comment.platform === "farcaster" && (
          <Button
            variant="ghost"
            className="px-2 hover:bg-transparent hover:cursor-default"
          >
            <Heart className="w-4 h-4 text-muted-foreground opacity-70" />
            <span className="sr-only">Like</span>
            <span className="text-xs ml-1 text-muted-foreground">
              {comment.farcasterLikesCount || 0}
            </span>
          </Button>
        )}

        {/* Currently, only Paragraph supports replies. */}
        {/* Don't allow a user to hit the reply button if they're at the max depth. */}
        {depth < MAX_DEPTH && comment.platform === "paragraph" && (
          <Button
            variant="ghost"
            className="px-2 mr-2"
            onClick={() => handleSetIsReplyingToCommentId(comment.id)}
            disabled={isReplyInProgress}
          >
            <MessageCircle className="w-4 h-4 text-muted-foreground opacity-70" />
            <span className="sr-only">Reply</span>
            <span className="text-xs ml-1 text-muted-foreground">
              {comment.replies?.length || 0}
            </span>
          </Button>
        )}
        {comment.paragraphIsOwner && (
          <div className="border-l h-6 flex items-center my-auto border-muted-foreground opacity-70 px-2 gap-2">
            <EditCommentButton
              onClick={() => handleSetIsEditingCommentId(comment.id)}
              isDisabled={isEditInProgress}
            />
            <DeleteCommentButton commentId={comment.id} refresh={refresh} />
          </div>
        )}
        {/* For Farcaster comments only, we have an external link button so you can click to go to the cast directly. */}
        {comment.platform === "farcaster" && comment.farcasterCastUrl && (
          <Link
            href={comment.farcasterCastUrl}
            target="_blank"
            className="flex items-center"
          >
            <Button variant="ghost" className="px-3 py-2 my-auto ">
              <ExternalLink className="w-4 h-4 text-muted-foreground opacity-70" />
              <span className="sr-only">Go to cast</span>
            </Button>
          </Link>
        )}
        {/* <Button variant="ghost" className="px-2">
          <Share className="w-4 h-4 text-muted-foreground opacity-70" />
          <span className="sr-only">Share</span>
        </Button> */}
      </div>

      {isEditingCommentId === comment.id && (
        <div className="my-2 w-full">
          <EditComment
            comment={comment}
            onSubmitStart={() => handleEditStart()}
            onSuccess={() => {
              handleEditEnd()
              handleSetIsEditingCommentId(comment.id)
            }}
          />
        </div>
      )}

      {isReplyingToCommentId === comment.id && (
        <div className="my-2 w-full">
          <AddComment
            postId={postId}
            replyToCommentId={comment.id}
            onSubmitStart={() => handleReplyStart()}
            onSuccess={() => {
              handleReplyEnd()
              handleSetIsReplyingToCommentId(comment.id)
            }}
          />
        </div>
      )}
    </CardFooter>
  )
}

function CommentThread(props: CommentProps) {
  const { comment, depth } = props

  const hasReplies =
    comment.replies && comment.replies.length > 0 && depth < MAX_DEPTH

  if (!hasReplies || depth >= MAX_DEPTH) {
    return null
  }

  return (
    <div className="ml-10 relative">
      {comment.replies?.map((reply) => (
        <Comment key={reply.id} {...props} depth={depth + 1} comment={reply} />
      ))}
    </div>
  )
}
